import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { FounderFull } from "@/components/about/FounderFull";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";
const FOUNDER_IMG = `${SITE}/founder-obrist-buasa.jpg`;

const META_TITLE = brandedTitle("About | Our Mission & Founder");
const META_DESC = safeDesc(
  "Heallexa is an AI-powered healthcare directory with 6M+ U.S. provider listings. Search by specialty, location, and insurance. Meet founder Obrist Buasa."
);

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: `${SITE}/about` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: FOUNDER_IMG },
      { name: "twitter:title", content: META_TITLE },
      { name: "twitter:description", content: META_DESC },
      { name: "twitter:image", content: FOUNDER_IMG },
    ],
    links: [{ rel: "canonical", href: `${SITE}/about` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": `${SITE}/about#webpage`,
          url: `${SITE}/about`,
          name: META_TITLE,
          description: META_DESC,
          isPartOf: { "@id": `${SITE}/#website` },
          about: { "@id": `${SITE}/#organization` },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["[data-speakable]"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Obrist Buasa",
          jobTitle: "Founder & CEO",
          url: "https://www.linkedin.com/in/obristbuasa/",
          image: FOUNDER_IMG,
          sameAs: ["https://www.linkedin.com/in/obristbuasa/"],
          worksFor: {
            "@type": "Organization",
            name: "Heallexa",
            url: SITE,
          },
          description:
            "Obrist Buasa is the Founder of Heallexa, an AI powered healthcare search platform created to make it easier for patients to find healthcare providers by specialty, location, insurance, and availability. Born in the Democratic Republic of Congo, he moved to the United States in 2011, holds a Bachelor's Degree in Business Administration, served in the United States Army from 2015 to 2019, and is a graduate of the Reinhard Bonnke School of Evangelism.",
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="pt-28 pb-12 md:pt-32 md:pb-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-primary border border-primary/30 px-3 py-1 mb-6">
              About Heallexa
            </span>
            <h1
              data-speakable
              className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground"
              style={{ lineHeight: 1.1, letterSpacing: "-0.03em" }}
            >
              Helping people find the right care, faster.
            </h1>
            <p
              data-speakable
              className="mt-5 text-lg text-muted-foreground"
              style={{ textWrap: "pretty" }}
            >
              Heallexa is an AI powered healthcare search platform with more than 6 million
              provider listings across the United States. We help patients search by
              specialty, location, insurance, and availability, and give providers a simple
              way to manage their online presence and receive appointment requests.
            </p>
          </div>
        </section>

        <div className="border-t border-border">
          <FounderFull />
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
